import * as turf from '@turf/turf';
import maplibregl from 'maplibre-gl';
import { type RefObject, useEffect, useRef } from 'react';
import type { UrbanNetworkEdge } from './api';

export const URBAN_NETWORK_SOURCE = 'smap-urban-network-source';
export const URBAN_NETWORK_LAYER = 'smap-urban-network-layer';
export const CANDIDATE_EDGE_SOURCE = 'smap-candidate-edge-source';
export const CANDIDATE_EDGE_LAYER = 'smap-candidate-edge-layer';
const DASHED_CURRENT_SOURCE = 'smap-dashed-current-source';
const DASHED_CURRENT_LAYER = 'smap-dashed-current-layer';
const DASHED_CANDIDATE_SOURCE = 'smap-dashed-candidate-source';
const DASHED_CANDIDATE_LAYER = 'smap-dashed-candidate-layer';
const DASHED_DRAFT_SOURCE = 'smap-dashed-draft-source';
const DASHED_DRAFT_LAYER = 'smap-dashed-draft-layer';
const DRAFT_MARKER_SOURCE = 'smap-draft-marker-source';
const DRAFT_MARKER_LAYER = 'smap-draft-marker-layer';

/** Amarillo suave = asociación actual (coherente con el resaltado de edge ya existente); celeste = candidato visual; violeta = marker/preview de "Nueva parada" (nunca los mismos colores que valid/needs_review/orphaned de las paradas, para no confundir semánticas). */
export const CURRENT_LINK_COLOR = '#ffe066';
export const CANDIDATE_LINK_COLOR = '#22d3ee';
export const DRAFT_COLOR = '#c792ea';

function buildEdgesFeatureCollection(edges: UrbanNetworkEdge[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: edges.map(e => ({
      type: 'Feature',
      geometry: e.geometry,
      properties: { id: e.id },
    })),
  };
}

/**
 * Capa "Red urbana" — READ ONLY, perezosa (el padre solo llama con
 * `enabled=true` después de pedir los datos una vez). Se inserta con
 * `beforeId: routesLayerId` para quedar SIEMPRE por debajo de
 * rutas/paradas, sin importar el orden en que los demás hooks agreguen
 * sus capas.
 */
export function useUrbanNetworkLayer(
  mapRef: RefObject<maplibregl.Map | null>,
  mapReady: boolean,
  enabled: boolean,
  edges: UrbanNetworkEdge[] | null,
  routesLayerId: string,
  onSelectEdge: (edgeId: string) => void
): void {
  const onSelectEdgeRef = useRef(onSelectEdge);
  onSelectEdgeRef.current = onSelectEdge;

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    if (!enabled || !edges) {
      if (map.getLayer(URBAN_NETWORK_LAYER)) map.removeLayer(URBAN_NETWORK_LAYER);
      if (map.getSource(URBAN_NETWORK_SOURCE)) map.removeSource(URBAN_NETWORK_SOURCE);
      return;
    }

    const data = buildEdgesFeatureCollection(edges);
    if (!map.getSource(URBAN_NETWORK_SOURCE)) {
      map.addSource(URBAN_NETWORK_SOURCE, { type: 'geojson', data });
      const beforeId = map.getLayer(routesLayerId) ? routesLayerId : undefined;
      map.addLayer(
        {
          id: URBAN_NETWORK_LAYER,
          type: 'line',
          source: URBAN_NETWORK_SOURCE,
          layout: { 'line-cap': 'round', 'line-join': 'round' },
          paint: { 'line-color': '#5c7a94', 'line-width': 1, 'line-opacity': 0.35 },
        },
        beforeId
      );
      map.on('click', URBAN_NETWORK_LAYER, e => {
        const id = e.features?.[0]?.properties?.id as string | undefined;
        if (id) onSelectEdgeRef.current(id);
      });
      map.on('mouseenter', URBAN_NETWORK_LAYER, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', URBAN_NETWORK_LAYER, () => {
        map.getCanvas().style.cursor = '';
      });
    } else {
      (map.getSource(URBAN_NETWORK_SOURCE) as maplibregl.GeoJSONSource).setData(data);
    }

    return (): void => {
      const m = mapRef.current;
      if (!m) return;
      try {
        if (m.getLayer(URBAN_NETWORK_LAYER)) m.removeLayer(URBAN_NETWORK_LAYER);
        if (m.getSource(URBAN_NETWORK_SOURCE)) m.removeSource(URBAN_NETWORK_SOURCE);
      } catch {
        /* mapa ya destruido */
      }
    };
  }, [mapRef, mapReady, enabled, edges, routesLayerId]);
}

/** Resalta el edge candidato (celeste) — mismo mecanismo que el resaltado del edge actual (amarillo, en SystemMapCanvas), color distinto para que la diferencia sea evidente. */
export function useCandidateEdgeHighlight(
  mapRef: RefObject<maplibregl.Map | null>,
  mapReady: boolean,
  candidateGeometry: GeoJSON.LineString | null
): void {
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    if (map.getLayer(CANDIDATE_EDGE_LAYER)) map.removeLayer(CANDIDATE_EDGE_LAYER);
    if (map.getSource(CANDIDATE_EDGE_SOURCE)) map.removeSource(CANDIDATE_EDGE_SOURCE);

    if (!candidateGeometry) return;

    map.addSource(CANDIDATE_EDGE_SOURCE, {
      type: 'geojson',
      data: { type: 'Feature', geometry: candidateGeometry, properties: {} },
    });
    map.addLayer({
      id: CANDIDATE_EDGE_LAYER,
      type: 'line',
      source: CANDIDATE_EDGE_SOURCE,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-width': 5, 'line-color': CANDIDATE_LINK_COLOR, 'line-opacity': 0.85 },
    });

    return (): void => {
      const m = mapRef.current;
      if (!m) return;
      try {
        if (m.getLayer(CANDIDATE_EDGE_LAYER)) m.removeLayer(CANDIDATE_EDGE_LAYER);
        if (m.getSource(CANDIDATE_EDGE_SOURCE)) m.removeSource(CANDIDATE_EDGE_SOURCE);
      } catch {
        /* mapa ya destruido */
      }
    };
  }, [mapRef, mapReady, candidateGeometry]);
}

function useDashedLink(
  mapRef: RefObject<maplibregl.Map | null>,
  mapReady: boolean,
  sourceId: string,
  layerId: string,
  color: string,
  from: [number, number] | null,
  to: [number, number] | null
): void {
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    if (map.getLayer(layerId)) map.removeLayer(layerId);
    if (map.getSource(sourceId)) map.removeSource(sourceId);

    if (!from || !to) return;

    map.addSource(sourceId, {
      type: 'geojson',
      data: { type: 'Feature', geometry: { type: 'LineString', coordinates: [from, to] }, properties: {} },
    });
    map.addLayer({
      id: layerId,
      type: 'line',
      source: sourceId,
      layout: { 'line-cap': 'round' },
      paint: { 'line-width': 2, 'line-color': color, 'line-opacity': 0.7, 'line-dasharray': [2, 2] },
    });

    return (): void => {
      const m = mapRef.current;
      if (!m) return;
      try {
        if (m.getLayer(layerId)) m.removeLayer(layerId);
        if (m.getSource(sourceId)) m.removeSource(sourceId);
      } catch {
        /* mapa ya destruido */
      }
    };
  }, [mapRef, mapReady, sourceId, layerId, color, from, to]);
}

/** Línea punteada parada→punto proyectado REAL (persistido) del edge actual — solo para la parada seleccionada, nunca las 1735 a la vez. */
export function useCurrentAssociationDashedLink(
  mapRef: RefObject<maplibregl.Map | null>,
  mapReady: boolean,
  stopPoint: [number, number] | null,
  projectedPoint: [number, number] | null
): void {
  useDashedLink(
    mapRef,
    mapReady,
    DASHED_CURRENT_SOURCE,
    DASHED_CURRENT_LAYER,
    CURRENT_LINK_COLOR,
    stopPoint,
    projectedPoint
  );
}

/** Línea punteada parada→punto proyectado del candidato — el punto se DERIVA en el momento (turf.nearestPointOnLine), nunca se persiste; es puramente visual. */
export function useCandidateAssociationDashedLink(
  mapRef: RefObject<maplibregl.Map | null>,
  mapReady: boolean,
  stopPoint: [number, number] | null,
  candidateGeometry: GeoJSON.LineString | null
): void {
  const projectedPoint =
    stopPoint && candidateGeometry
      ? (turf.nearestPointOnLine(candidateGeometry, stopPoint).geometry.coordinates as [number, number])
      : null;
  useDashedLink(
    mapRef,
    mapReady,
    DASHED_CANDIDATE_SOURCE,
    DASHED_CANDIDATE_LAYER,
    CANDIDATE_LINK_COLOR,
    stopPoint,
    projectedPoint
  );
}

/** Distancia real (metros) de la parada al edge candidato — derivada solo para mostrar en el panel, nunca guardada. */
export function candidateDistanceMeters(
  stopPoint: [number, number] | null,
  candidateGeometry: GeoJSON.LineString | null
): number | null {
  if (!stopPoint || !candidateGeometry) return null;
  const nearest = turf.nearestPointOnLine(candidateGeometry, stopPoint);
  return turf.distance(stopPoint, nearest.geometry.coordinates, { units: 'meters' });
}

/** Vuela hacia `stopPoint` solo cuando `focusToken` cambia (Anterior/Siguiente/Iniciar de "Revisar pendientes") — un click manual sobre una parada NO debe forzar zoom, por eso no depende directamente de `stopPoint`. */
export function useFlyOnFocus(
  mapRef: RefObject<maplibregl.Map | null>,
  mapReady: boolean,
  stopPoint: [number, number] | null,
  focusToken: number
): void {
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !stopPoint || focusToken === 0) return;
    map.flyTo({ center: stopPoint, zoom: 17, speed: 1.4 });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- se dispara deliberadamente solo por focusToken, no por cada cambio de stopPoint
  }, [mapRef, mapReady, focusToken]);
}

/** Marker provisional del flujo "Nueva parada" — violeta, distinto de cualquier estado QA real, para que nunca se confunda con una parada persistida. */
export function useDraftMarkerLayer(
  mapRef: RefObject<maplibregl.Map | null>,
  mapReady: boolean,
  draftPoint: [number, number] | null
): void {
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    if (map.getLayer(DRAFT_MARKER_LAYER)) map.removeLayer(DRAFT_MARKER_LAYER);
    if (map.getSource(DRAFT_MARKER_SOURCE)) map.removeSource(DRAFT_MARKER_SOURCE);

    if (!draftPoint) return;

    map.addSource(DRAFT_MARKER_SOURCE, {
      type: 'geojson',
      data: { type: 'Feature', geometry: { type: 'Point', coordinates: draftPoint }, properties: {} },
    });
    map.addLayer({
      id: DRAFT_MARKER_LAYER,
      type: 'circle',
      source: DRAFT_MARKER_SOURCE,
      paint: {
        'circle-radius': 8,
        'circle-color': DRAFT_COLOR,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#06131f',
        'circle-opacity': 0.9,
      },
    });

    return (): void => {
      const m = mapRef.current;
      if (!m) return;
      try {
        if (m.getLayer(DRAFT_MARKER_LAYER)) m.removeLayer(DRAFT_MARKER_LAYER);
        if (m.getSource(DRAFT_MARKER_SOURCE)) m.removeSource(DRAFT_MARKER_SOURCE);
      } catch {
        /* mapa ya destruido */
      }
    };
  }, [mapRef, mapReady, draftPoint]);
}

/** Línea punteada marker provisional→edge propuesto/override manual — mismo mecanismo que el candidato de edición, fuente/capa separadas para poder convivir si algún día se combinan flujos. */
export function useDraftAssociationDashedLink(
  mapRef: RefObject<maplibregl.Map | null>,
  mapReady: boolean,
  draftPoint: [number, number] | null,
  edgeGeometry: GeoJSON.LineString | null
): void {
  const projectedPoint =
    draftPoint && edgeGeometry
      ? (turf.nearestPointOnLine(edgeGeometry, draftPoint).geometry.coordinates as [number, number])
      : null;
  useDashedLink(mapRef, mapReady, DASHED_DRAFT_SOURCE, DASHED_DRAFT_LAYER, DRAFT_COLOR, draftPoint, projectedPoint);
}

/**
 * Click genérico en el mapa mientras el modo "Nueva parada" está armado.
 * Se ignora si el click cayó sobre una parada o un edge de Red urbana
 * (esos ya tienen su propio handler — clickear una parada existente nunca
 * debe abrir el flujo de creación encima).
 */
export function useMapClickForPlacement(
  mapRef: RefObject<maplibregl.Map | null>,
  mapReady: boolean,
  armed: boolean,
  featureLayerIds: string[],
  onPlace: (lon: number, lat: number) => void
): void {
  const onPlaceRef = useRef(onPlace);
  onPlaceRef.current = onPlace;
  const featureLayerIdsRef = useRef(featureLayerIds);
  featureLayerIdsRef.current = featureLayerIds;

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !armed) return;

    function handleClick(e: maplibregl.MapMouseEvent): void {
      const map2 = mapRef.current;
      if (!map2) return;
      const existingLayers = featureLayerIdsRef.current.filter(id => map2.getLayer(id));
      if (existingLayers.length > 0) {
        const hit = map2.queryRenderedFeatures(e.point, { layers: existingLayers });
        if (hit.length > 0) return;
      }
      onPlaceRef.current(e.lngLat.lng, e.lngLat.lat);
    }

    map.on('click', handleClick);
    return (): void => {
      map.off('click', handleClick);
    };
  }, [mapRef, mapReady, armed]);
}
